// Catalog Shell - Core Type Definitions
// ============================================================================

// Version for configuration format
import { DisposableStore } from '../components/disposable';
import type { ActionRegistry } from './action-registry';


export const SHELL_VERSION = { major: 1, minor: 0 };

// ============================================================================
// Screen Size & Breakpoints
// ============================================================================

export type ScreenSize = 'compact' | 'medium' | 'full';

export interface Breakpoints {
    compact: number;  // Max width for compact (default: 800)
    medium: number;   // Max width for medium (default: 1200), above = full
}

export const DEFAULT_BREAKPOINTS: Breakpoints = {
    compact: 800,
    medium: 1200
};

// ============================================================================
// Pane Taxonomy
// ============================================================================

export type PaneKind = 'container' | 'leaf';
export type ContainerLayout = 'grid' | 'flow';

// ============================================================================
// Pane Instance Metadata
// ============================================================================

export interface PaneInstanceFlags {
    reparentable: boolean;  // Can be dragged out of its current Surface
    cloneable: boolean;     // Can be cloned on Alt-drag (future)
}

export interface PaneInstanceContext {
    [key: string]: unknown;  // e.g., { cameraId: 'cam-a' }
}

export interface PaneInstance {
    instanceId: string;           // Unique instance ID
    typeId: string;               // Reference to PaneType.typeId
    title: string;                // Display title (can be overridden from type default)
    context: PaneInstanceContext; // Instance-specific context (e.g., cameraId)
    flags: PaneInstanceFlags;     // Instance behavior flags
    element?: HTMLElement;        // Rendered DOM element (cached)
    parentSurfaceId?: string;     // ID of containing surface (for nested panes)
    isDetached?: boolean;         // True when pane is removed from a cell but kept alive in stash
    isDynamic?: boolean;          // True when pane was opened via openPane (not part of initial layout)
    data?: Record<string, unknown>; // Arbitrary data for pane type use (lifecycle, cleanup, etc.)
}

// ============================================================================
// Pane Type Definition
// ============================================================================

export interface PaneTypeRenderContext {
    instance: PaneInstance;
    shell: ShellAPI;
    surface?: SurfaceAPI;  // For container panes: the surface being rendered into
    /** Auto-managed disposable store. Disposed when pane is destroyed. */
    disposables: DisposableStore;
}

export interface PaneType {
    typeId: string;                           // Unique type identifier
    kind: PaneKind;                           // 'container' or 'leaf'
    containerLayout?: ContainerLayout;        // For containers: 'grid' or 'flow'
    defaultTitle: string;                     // Default title for new instances
    capabilities: string[];                   // Declared capabilities (e.g., ['selection', 'export'])
    defaultFlags?: Partial<PaneInstanceFlags>; // Default flags for new instances
    eagerRender?: boolean;                    // When true, render even on inactive tabs (for panes that need background initialization)
    canCopyToPopup?: boolean;                 // Whether pane can be copied to popup window. Default: true
    defaultWidth?: number;                    // Preferred width in pixels for flow surfaces (default: 750)
    canShrink?: boolean;                      // Flow surfaces: pane may shrink to 80% of ideal width (default: true)
    stretch?: boolean;                        // Flow surfaces: pane absorbs all remaining width (default: false)
    render: (ctx: PaneTypeRenderContext) => HTMLElement;

    /**
     * Optional elements to display in the cell tab bar actions area when this
     * pane is active. Also applies to a module container (`kind: 'container'`
     * that is not a grid/flow surface) used as a workspace root: the shell
     * mounts a `.cs-workspace-root-header` title strip above the root and puts
     * these there.
     */
    tabBarActions?: (instance: PaneInstance) => HTMLElement[];

    // Optional lifecycle hooks
    onActivate?: (instance: PaneInstance) => void;
    onDeactivate?: (instance: PaneInstance) => void;
    onDetach?: (instance: PaneInstance) => void;   // Called when pane is removed from a cell but kept alive (e.g., to pause resources)
    onAttach?: (instance: PaneInstance) => void;    // Called when a detached pane is re-added to a cell (e.g., to resume resources)
    onDestroy?: (instance: PaneInstance) => void;

    /**
     * Optional guard invoked before a user-initiated close of this pane (tab
     * close button, context-menu Remove). Return false — or a Promise that
     * resolves to false — to cancel the close. Not consulted for programmatic
     * closePane() during teardown, workspace switches, or cascade closes.
     */
    confirmClose?: (instance: PaneInstance) => boolean | Promise<boolean>;
}

// ============================================================================
// Surface API (exposed to panes inside a container)
// ============================================================================

export interface SurfaceAPI {
    surfaceId: string;
    parentSurface?: SurfaceAPI;  // For popout surfaces: reference to originating surface
    getSurfaceType(): ContainerLayout;

    // Pane management
    getPanes(): PaneInstance[];
    getActivePane(): PaneInstance | null;
    setActivePane(instanceId: string): void;

    // Opening new panes (used by FlowSurface primarily)
    openPane(spec: PaneInstanceSpec, options?: OpenPaneOptions): PaneInstance;
    closePane(instanceId: string): void;

    // Pane updates
    updatePaneTitle(instanceId: string, title: string): void;
    /**
     * Re-render a pane's title-row actions (`PaneType.tabBarActions`) in place
     * when its state changes. Implemented by FlowSurface and GridSurface;
     * optional so custom surfaces need not. Prefer `shell.refreshPaneActions`,
     * which routes to whichever surface owns the pane.
     */
    refreshPaneActions?(instanceId: string): void;

    // Query
    getPaneById(instanceId: string): PaneInstance | null;
}

export interface OpenPaneOptions {
    /**
     * The pane this one is opened from. Flow: insert right after it and
     * cascade-close everything beyond. Grid: open in the origin's cell as the
     * tab right after it (see `placement`).
     */
    originPaneId?: string;
    /**
     * Grid only: where relative to the origin. `'origin'` (default) is the
     * origin's own cell; `'adjacent'` is the cell to its right in the same
     * row, for master–detail layouts, falling back to the origin's cell when
     * there is none. Ignored without `originPaneId`.
     */
    placement?: 'origin' | 'adjacent';
    /** Grid only: an explicit target cell. Wins over `originPaneId`. */
    position?: CellPosition;
    activate?: boolean;      // Whether to activate the new pane (default: true)
}

export interface CellPosition {
    row: number;
    col: number;
}

export interface PaneInstanceSpec {
    typeId: string;
    title?: string;  // Override default title
    context?: PaneInstanceContext;
    flags?: Partial<PaneInstanceFlags>;
    nestedLayoutConfig?: GridLayoutConfig | FlowLayoutConfig;  // For container panes: their internal layout
    isDynamic?: boolean;  // Saved with the layout so a pane the user opened keeps its close button across a reload
}

// ============================================================================
// Shell API (global shell access)
// ============================================================================

export interface ShellAPI {
    // Initialization
    start(): void;

    // Workspace management
    getActiveWorkspace(): string;
    setActiveWorkspace(workspaceId: string): void;
    /** The surface that IS a workspace (its root), or null before the
     *  workspace has been laid out. A module verb that lives in one workspace
     *  but is reached from another opens its pane here. */
    getWorkspaceRootSurface(workspaceId: string): SurfaceAPI | null;
    getWorkspaces(): string[];
    getWorkspaceLabel(workspaceId: string): string;
    setWorkspaceHidden(workspaceId: string, hidden: boolean): void;

    // Screen size
    getScreenSize(): ScreenSize;
    setScreenSizeOverride(size: ScreenSize | null): void;

    // Pane type registry
    getPaneType(typeId: string): PaneType | null;
    registerPaneType(type: PaneType): void;

    // Action system
    getActionRegistry(): ActionRegistry;
    registerAction(action: ActionDefinition): void;
    executeAction(actionId: string, context?: ActionContext): void;
    getAvailableActions(context?: ActionContext): ActionDefinition[];

    // Global pane instance lookup
    getPaneInstance(instanceId: string): PaneInstance | null;
    getSurfaceForPane(instanceId: string): SurfaceAPI | null;

    // Render context factory
    createRenderContext(instance: PaneInstance, surface?: SurfaceAPI): PaneTypeRenderContext;

    // Surface access
    getRootSurface(): SurfaceAPI | null;
    getSurface(surfaceId: string): SurfaceAPI | null;

    // Events
    on(event: ShellEvent, callback: ShellEventCallback): () => void;
    emit(event: ShellEvent, data?: unknown): void;

    // Action bar
    refreshActionBar(): void;
    addActionBarElement(element: HTMLElement): void;

    /**
     * Re-run a pane's `tabBarActions` and swap the result into its title row
     * (flow header / grid tab bar). Call it when the state those actions read
     * has changed — after an async load, a status event, a mode switch.
     * No-op for a pane whose surface does not support it.
     */
    refreshPaneActions(instanceId: string): void;

    // Persistence
    saveConfiguration(): void;
    exportConfiguration(): ExportedShellConfig;
    importConfiguration(config: ExportedShellConfig): ImportResult;

    // Standalone popouts
    openStandalonePopout(config: StandalonePopoutConfig): void;

    // Modal API
    showModal(content: HTMLElement, options?: import('./modal-manager').ModalOptions): import('./modal-manager').ModalHandle;
    confirm(options: import('./modal-manager').ConfirmOptions): Promise<boolean>;
    /**
     * One-input dialog: resolves to the entered text, or `null` on
     * cancel/dismiss. The sibling of `confirm` for a verb that needs a reason
     * or a type-to-confirm (`mustEqual`) — never an overload of it.
     */
    prompt(options: import('./modal-manager').PromptOptions): Promise<string | null>;
    closeModal(): void;
    closeAllModals(): void;

    // Toast API
    toast(options: import('./toast-manager').ToastOptions): import('./toast-manager').ToastHandle;
    toast(message: string, variant?: 'info' | 'success' | 'warning' | 'error'): import('./toast-manager').ToastHandle;
}

export interface StandalonePopoutConfig {
    title: string;
    width?: number;
    height?: number;
    content: (container: HTMLElement, popupWindow: Window) => (() => void) | void;
}

export type ShellEvent =
    | 'workspace-changed'
    | 'workspace-closed'
    | 'screen-size-changed'
    | 'pane-activated'
    | 'pane-opened'
    | 'pane-closed'
    | 'layout-changed'
    | 'action-executed'
    | 'data-changed';

export type ShellEventCallback = (data: unknown) => void;

// ============================================================================
// Workspace Definition
// ============================================================================

export interface WorkspaceDefinition {
    id: string;
    label: string;
    enabledSizes?: ScreenSize[];  // Empty = on-demand only
    rootPaneSpec: PaneInstanceSpec;  // The root pane (typically a container)
    rootLayoutConfig?: GridLayoutConfig | FlowLayoutConfig;  // Initial layout for root (deprecated, use layouts)
    layouts?: {
        compact?: GridLayoutConfig | FlowLayoutConfig;
        medium?: GridLayoutConfig | FlowLayoutConfig;
        full?: GridLayoutConfig | FlowLayoutConfig;
    };  // Layouts per screen size for responsive breakpoint switching
    /**
     * Type IDs recommended for this workspace: the grid surface highlights
     * them in its context menu, and a flow surface with no stored layout seeds
     * itself with the first registered leaf among them. It is NOT the add-pane
     * picker's roster — see `pickerPanes`.
     */
    recommendedPanes?: string[];
    /**
     * Roster of the flow surface's add-pane picker (`+` and background
     * right-click): context-free leaf pane types only — never a record pane
     * that needs a `context`. Absent ⇒ the picker is not offered.
     */
    pickerPanes?: string[];
    /** When true, the switcher shows a close (×) on this workspace's tab
     *  (used for dynamic editor workspaces). Default false. */
    closable?: boolean;
    /** Sort order in the switcher (ascending; default 0). Lets dynamic
     *  workspaces slot between fixed ones (e.g. editors before a trailing Agent). */
    order?: number;
}

// ============================================================================
// Grid Surface Layout Configuration
// ============================================================================

export interface GridLayoutConfig {
    type: 'grid';
    columnCount: number;
    columnSizes: number[];   // Percentages, must sum to 100
    rowSizes: number[];      // Percentages, must sum to 100
    cells: GridCellConfig[];
    configuredPaneTypes?: string[];  // Explicit list of pane type IDs available in this surface
}

export interface GridCellConfig {
    cellId: string;
    rowStart: number;    // 0-based
    rowEnd: number;      // Exclusive
    colStart: number;    // 0-based
    colEnd: number;      // Exclusive
    panes: PaneInstanceSpec[];
    activePaneIndex: number;
}

// ============================================================================
// Flow Surface Layout Configuration
// ============================================================================

export interface FlowLayoutConfig {
    type: 'flow';
    panes: FlowPaneConfig[];
    activePaneIndex?: number;
}

export interface FlowPaneConfig {
    paneSpec: PaneInstanceSpec;
    width?: number;  // Pixels, default: 750
}

// ============================================================================
// Action System
// ============================================================================

/**
 * Where a registered action renders. `toolbar` = the shell's top bar, `menu`
 * = its gear dropdown. `contextmenu` and `palette` are RESERVED: declared for
 * forward compatibility, no surface renders them today.
 */
export type ActionLocation = 'toolbar' | 'menu' | 'contextmenu' | 'palette';

export interface ActionDefinition {
    actionId: string;
    label: string;
    icon?: string;                      // Icon name or URL
    shortcut?: string;                  // Keyboard shortcut
    locations: ActionLocation[];        // Where action appears
    requiredCapabilities: string[];     // Capabilities required from focused pane
    contextPredicate?: (ctx: ActionContext) => boolean;  // Additional visibility check
    /**
     * Only shown while one of these workspaces is active; omit for every
     * workspace. Use it for a module-wide verb that is only meaningful in one
     * workspace — never to fake a pane-level action (that is a title-row
     * action; see USAGE.md "Where a button belongs").
     */
    workspaces?: string[];
    /**
     * A write this action started is still in flight: return what it is doing
     * ("Syncing wallets…") and the bar renders the action DISABLED with that
     * string as its label and tooltip, in the toolbar and in the gear menu
     * alike (rule 4). Return `undefined` when idle. Sugar over disabled +
     * title, not a second mechanism — the module clears its own flag and calls
     * `shell.refreshActionBar()` when the write settles.
     */
    busy?: (ctx: ActionContext) => string | undefined;
    /**
     * Why this action is greyed out right now: return the reason and the bar
     * uses it as the tooltip of the disabled button and gear item, so a verb
     * a sibling write has blocked can say so (rule 3). Read ONLY while
     * `isEnabled` returns false, and only when the action is not `busy` —
     * `busy` is what THIS action is doing and always wins. Return `undefined`
     * for the plain label/shortcut tooltip.
     */
    disabledReason?: (ctx: ActionContext) => string | undefined;
    execute: (ctx: ActionContext) => void | Promise<void>;
    isEnabled?: (ctx: ActionContext) => boolean;  // Dynamic enabled state
}

export interface ActionContext {
    focusedPane?: PaneInstance;
    focusedSurface?: SurfaceAPI;
    selectedItems?: unknown[];          // Generic selection from focused pane
    capabilities: string[];             // Capabilities of the focused pane's PaneType
    shell: ShellAPI;
}

// ============================================================================
// Configuration & Persistence
// ============================================================================

export interface ShellConfig {
    breakpoints?: Breakpoints;
    workspaces: WorkspaceDefinition[];
    defaultWorkspace: string;
    devToolsEnabled?: boolean;
    /** Bump to discard stored layouts when workspace configs change */
    configVersion?: number;
    /** Called during export to let the app attach custom data (e.g. profiles). */
    onBeforeExport?: () => Record<string, unknown>;
    /** Called during import with the app data from the exported config. */
    onAfterImport?: (appData: Record<string, unknown>) => void;
}

export interface StoredShellConfig {
    version: { major: number; minor: number };
    configVersion?: number;
    activeWorkspace: string;
    screenSizeOverride?: ScreenSize | null;
    workspaceConfigs: {
        [workspaceId: string]: StoredWorkspaceConfig;
    };
    /** Runtime-added (dynamic) workspaces — e.g. on-demand editors — so they
     *  reappear (with their label + root context) after a reload. */
    runtimeWorkspaces?: WorkspaceDefinition[];
    popoutWindows?: StoredPopoutWindow[];
}

export interface StoredWorkspaceConfig {
    layouts?: {
        compact?: GridLayoutConfig | FlowLayoutConfig;
        medium?: GridLayoutConfig | FlowLayoutConfig;
        full?: GridLayoutConfig | FlowLayoutConfig;
    };
    customized?: boolean;
}

export interface StoredPopoutWindow {
    id: string;
    parentSurfaceId: string;
    workspaceId: string;
    layout: FlowLayoutConfig;
    windowFeatures: { width: number; height: number; left: number; top: number };
    /** Maps pane typeId → source cellId for reattachment after popout close */
    sourceCells?: Record<string, string>;
}

export interface ExportedShellConfig {
    version: { major: number; minor: number };
    config: StoredShellConfig;
    exportedAt: string;
    appData?: Record<string, unknown>;
}

export interface ImportResult {
    success: boolean;
    error?: string;
    warnings?: string[];
}

// ============================================================================
// Article Pane Content Model
// ============================================================================

export type ArticleBlock =
    | ArticleParagraphBlock
    | ArticleHeadingBlock
    | ArticleMediaBlock
    | ArticleCodeBlock;

export interface ArticleParagraphBlock {
    type: 'paragraph';
    text: string;  // Plain text or simple markdown
}

export interface ArticleHeadingBlock {
    type: 'heading';
    level: 1 | 2 | 3 | 4 | 5 | 6;
    text: string;
}

export interface ArticleMediaBlock {
    type: 'media';
    mediaType: 'image' | 'video' | 'placeholder';
    src?: string;
    alt?: string;
    caption?: string;
    width?: number;
    height?: number;
}

export interface ArticleCodeBlock {
    type: 'code';
    language?: string;
    code: string;
}

export interface ArticleContent {
    blocks: ArticleBlock[];
}

// ============================================================================
// Drag & Drop Types
// ============================================================================

export interface DragData {
    dragType: 'pane-tab' | 'pane-move' | 'surface-tab';
    instanceId: string;
    sourceSurfaceId: string;
    paneKind: PaneKind;
    isReparentable: boolean;
}

export interface DropTarget {
    targetType: 'cell-body' | 'tab-bar' | 'edge-split';
    surfaceId: string;
    cellId?: string;
    edge?: 'top' | 'bottom' | 'left' | 'right';
    insertIndex?: number;  // For tab bar drops
}

// ============================================================================
// Utility Types
// ============================================================================

export type DeepPartial<T> = {
    [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export function generateId(prefix: string = 'id'): string {
    return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 9)}`;
}
